import { CalendarType } from '@/model/calendartype.model';
import { CalEvent } from '@/model/calevent.model';
import { CalRow } from '@/model/calrow.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { ligaNews } from '@/model/liganews.model';
import { ReplaceCounter } from '@/model/replacecounter.model';
import router from '@/router/index';
import store from '@/store/index';
import buildPaginationQueryOpts from '@/utils/sorts';
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(isoWeek);
dayjs.extend(localeData);
dayjs.locale('ru');
dayjs().isoWeekday(1);

export default class CalendarService {

  public weekdaysMin = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
  public weekdaysMax = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
  public monthArray = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  public monthArrayRP = ['Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня', 'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'];
  public currentMonth = dayjs().month();
  public currentMonthName = this.monthArray[dayjs().month()];
  public currentYear = dayjs().year();
  public globalContext?: GlobalContext;

  constructor() {
  }

  public loadSummary(): Promise<Array<any>> {
    return this.getCalendarSummary();
  }

  public getDateDay(inDate: string): number {
    try {
      const dateParts = inDate.split('.');
      const inDayJs = dayjs().year(Number(dateParts[2])).month(Number(dateParts[1]) - 1).date(Number(dateParts[0]));
      return inDayJs.isoWeekday();
    } catch (ex) {
      return -1;
    }
  }

  public formatDate(_date: Date): string {
    if (!_date) return "";

    return (
      this.addDigit(dayjs(_date).date().toString()) +
      "." +
      this.addDigit((dayjs(_date).month() + 1).toString()) +
      "." +
      dayjs(_date).year().toString()
    );
  }

  private addDigit(value: string): string {
    return ("0" + value).slice(-2);
  }

  public getDateDayName(inDate: string): string {
    return this.weekdaysMax[this.getDateDay(inDate) - 1];
  }

  public async getCalendarSummary(month?: number, year?: number, sector?: string): Promise<Array<CalRow>> {
    let calendar = new Array<CalRow>();
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(calendar);
      });
    }

    const requestParams = {
      instructor: (store.getters.account ? store.getters.account.lastName + ' ' + store.getters.account.firstName : ''),
      month: (typeof month !== 'undefined' ? String(month + 1) : String(dayjs().month() + 1)),
      year: (typeof year !== 'undefined' ? String(year) : String(dayjs().year())),
      sector: typeof sector !== 'undefined' ? sector : ''
    };

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      const options = {
        url: `${process.env.VUE_APP_API_URL}api/google/calendar-summary`,
        params: requestParams,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      };

      return new Promise(resolve => {
        CapacitorHttp.get(options).then(async response => {
          calendar = response.data;
          resolve(calendar);
        }).catch(() => {
          router.replace('/badconnection');
          resolve(calendar);
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .get('api/google/calendar-summary', {
            params: requestParams,
          })
          .then(async response => {
            calendar = response.data;
            resolve(calendar);
          })
          .catch(() => {
            router.replace('/badconnection');
            resolve(calendar);
          });
      });
    }
  }
  public async getCalendarSummaryCount(month?: number, year?: number, sector?: string, byRating?: number): Promise<number> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(0);
      });
    }

    const requestParams = {
      instructor: (store.getters.account ? store.getters.account.lastName + ' ' + store.getters.account.firstName : ''),
      month: (typeof month !== 'undefined' ? String(month + 1) : String(dayjs().month() + 1)),
      year: (typeof year !== 'undefined' ? String(year) : String(dayjs().year())),
      sector: typeof sector !== 'undefined' ? sector : '',
      byRating: typeof byRating !== 'undefined' ? String(byRating) : '-1'
    };

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const options = {
      url: `${process.env.VUE_APP_API_URL}api/google/calendar-summary-count`,
      params: requestParams,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise(resolve => {
      CapacitorHttp.get(options).then(async response => {
        resolve(Number(response.data));
      }).catch(() => {
        resolve(0);
      })
    });
  }

  public async getCalendarEvents(calName: string): Promise<Array<CalEvent>> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const options = {
      url: `${process.env.VUE_APP_API_URL}api/calendar-kind-events`,
      params: {
        calName: calName
      },
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise(resolve => {
      CapacitorHttp.get(options).then(async response => {
        resolve(response.data);
      }).catch((err) => {
        console.error('Failed to get calendar events', err);
        resolve(new Array<CalEvent>());
      })
    });
  }

  public async getAllCalendarEvents(): Promise<Array<CalEvent>> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const options = {
      url: `${process.env.VUE_APP_API_URL}api/calendar-events`,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise(resolve => {
      CapacitorHttp.get(options).then(async response => {
        resolve(response.data);
      }).catch((err) => {
        console.error('Failed to get calendar events', err);
        resolve(new Array<CalEvent>());
      })
    });
  }

  public async getCalendarEventsStartedFrom(startDate: string): Promise<Array<CalEvent>> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const options = {
      url: `${process.env.VUE_APP_API_URL}api/calendar-events/start`,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
      params: {
        startDate: startDate
      },
    };

    return new Promise(resolve => {
      CapacitorHttp.get(options).then(async response => {
        resolve(response.data);
      }).catch((err) => {
        console.error('Failed to get calendar events', err);
        resolve(new Array<CalEvent>());
      })
    });
  }

  public async getTodayEventsCount(calendarsDic: Array<CalendarType>): Promise<number> {
    return new Promise(resolve => {
      this.getCalendarEventsStartedFrom(this.formatDate(new Date())).then(result => {
        resolve(result.filter(event => {
          return event.startMonth !== undefined &&
            event.kind !== undefined &&
            undefined !==
            calendarsDic.find((cd) => {
              return cd.name == event.kind;
            }) &&
            event.startDate !== undefined &&
            event.startDate === this.formatDate(new Date())
        }).length
        );
      })
    })
  }

  public async getUpcomingCalendarEventsCount(kinds: string): Promise<number> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const options = {
      url: `${process.env.VUE_APP_API_URL}api/calendar-events/upcoming/{kinds}/count`,
      params: {
        kinds: kinds
      },
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise(resolve => {
      CapacitorHttp.get(options).then(async response => {
        resolve(response.data);
      }).catch((err) => {
        console.error('Failed to get count of upcoming calendar events', err);
        resolve(0);
      })
    });
  }

  public async removeCalendarEvent(event: CalEvent): Promise<boolean> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const deleteOptions = {
      url: `${process.env.VUE_APP_API_URL}api/calendar-events/${event.id}`,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise(resolve => {
      if (event.id !== '') {
        CapacitorHttp.delete(deleteOptions).then(() => {
          resolve(true);
        }).catch((err) => {
          console.error('Failed to delete calendar event', err);
          resolve(false);
        })
      }
    });
  }

  public async countAllReplacements(): Promise<ReplaceCounter> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    return new Promise<ReplaceCounter>(resolve => {
      CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_URL}api/month-sheets/need-replace/count`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000
      }).then((response) => {
        const replaceCounter = new ReplaceCounter(response.data.countNeedReplace || 0, response.data.countNeedAccept || 0);
        resolve(replaceCounter);
        if (this.globalContext) {
          this.globalContext.showMenuNotification = response.data.countNeedReplace > 0;
        }
      }).catch((err) => {
        console.error('Failed to get replacements', err);
        resolve(new ReplaceCounter(0, 0));
        if (this.globalContext) {
          this.globalContext.showMenuNotification = false;
        }
      })
    });
  }

  public async countFilteredReplacements(startDate: string, endDate: string): Promise<ReplaceCounter> {
    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;
    const requestParams = {
      startDate: startDate,
      endDate: endDate,
    }

    return new Promise<ReplaceCounter>(resolve => {
      CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_URL}api/month-sheets/need-replace/count/filter`,
        headers: { Authorization: `Bearer ${token}` },
        params: requestParams,
        readTimeout: 60000
      }).then((response) => {
        const replaceCounter = new ReplaceCounter(response.data.countNeedReplace || 0, response.data.countNeedAccept || 0);
        resolve(replaceCounter);
      }).catch((err) => {
        console.error('Failed to get replacements', err);
        resolve(new ReplaceCounter(0, 0));
      })
    });
  }

  public async todayNewsCount(): Promise<number> {
    let ligaNews = new Array<ligaNews>();
    let selectedNewsTotal = 0;

    const tokenQuery = await Preferences.get({ key: "jwt" });
    const token = tokenQuery.value;
    const req = {
      page: 0,
      size: 50,
      sort: ["publicDate,desc"],
    };
    const options = {
      url: `${process.env.VUE_APP_API_URL
        }api/news?${buildPaginationQueryOpts(req)}`,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise<number>(resolve => {
      CapacitorHttp.get(options)
        .then(async (response) => {
          if (response) {
            ligaNews = response.data;
            ligaNews = ligaNews.filter((news) => {
              return news.publicDate
                ? new Date(Date.parse(news.publicDate)) <= new Date()
                : false;
            });
            ligaNews.map((news) => {
              try {
                if (news.publicDate) {
                  const parsedPublicDate = new Date(Date.parse(news.publicDate));
                  news.publicDateConverted =
                    dayjs(parsedPublicDate).format("DD.MM.YYYY HH:mm");
                  if (
                    parsedPublicDate <= new Date() &&
                    parsedPublicDate >= dayjs().startOf("day").toDate()
                  ) {
                    selectedNewsTotal += 1;
                  }
                }
              } catch (ex) {
                //ignore
              }
            });
          }
        })
        .catch((err) => {
          console.error("Failed to get calendar events", err);
        }).finally(() => {
          resolve(selectedNewsTotal);
        });
    })
  }

  public async todayNews(): Promise<Array<ligaNews>> {
    let ligaNews = new Array<ligaNews>();

    const tokenQuery = await Preferences.get({ key: "jwt" });
    const token = tokenQuery.value;
    const req = {
      page: 0,
      size: 10,
      sort: ["publicDate,desc"],
    };
    const options = {
      url: `${process.env.VUE_APP_API_URL
        }api/news?${buildPaginationQueryOpts(req)}`,
      headers: { Authorization: `Bearer ${token}` },
      readTimeout: 60000,
    };

    return new Promise<Array<ligaNews>>(resolve => {
      CapacitorHttp.get(options)
        .then(async (response) => {
          if (response) {
            ligaNews = response.data;
            ligaNews = ligaNews.filter((news) => {
              return news.publicDate
                ? new Date(Date.parse(news.publicDate)) <= new Date()
                : false;
            });

            ligaNews.map((news) => {
              try {
                if (news.publicDate) {
                  const parsedPublicDate = new Date(Date.parse(news.publicDate));
                  news.publicDateConverted =
                    dayjs(parsedPublicDate).format("DD.MM.YYYY HH:mm");
                }
              } catch (ex) {
                //ignore
              }
            });
          }
        })
        .catch((err) => {
          console.error("Failed to get calendar events", err);
        }).finally(() => {
          resolve(ligaNews);
        });
    })
  }
}